import React from 'react';
import { Link } from 'react-router-dom';
import { defaultCourseFeeForSections, feeBreakdownLabel, multiCourseDiscountPercent } from '@/pages/trainingFees';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { HearAboutSelect } from '@/components/training/HearAboutSelect';

/** "Add one trainee" modal + the duplicate-email confirmation that can follow it. */
export function AddTraineeDialog({ addOne, addingOne, busy, data, defaultFeeStr, duplicateInfo, form, otherCourses, palette, setAddingOne, setDuplicateInfo, setForm, settings }) {
  return (
    <>
      <Dialog open={addingOne} onOpenChange={(o) => !busy && setAddingOne(o)}>
        <DialogContent
          data-testid="training-add-one-modal"
          className="!bg-white max-h-[90vh] overflow-y-auto"
          style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: palette.dark }}>Add Trainee</DialogTitle>
            <DialogDescription>Their email will be matched against Ox Resin accounts when they confirm on the day.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="t-name">Name</Label>
              <Input id="t-name" data-testid="trainee-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} autoComplete="off" className="bg-white" />
            </div>
            <div>
              <Label htmlFor="t-email">Email</Label>
              <Input id="t-email" data-testid="trainee-email" type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} autoComplete="off" className="bg-white" />
            </div>
            <div>
              <Label htmlFor="t-phone">Phone (optional)</Label>
              <Input id="t-phone" data-testid="trainee-phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} autoComplete="off" className="bg-white" />
            </div>
            <HearAboutSelect id="t-hear-about" value={form.hear_about} onChange={(v) => setForm(f => ({ ...f, hear_about: v }))} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="t-total">Course fee (£)</Label>
                <Input
                  id="t-total"
                  data-testid="trainee-payment-total"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.payment_total}
                  onChange={(e) => setForm(f => ({ ...f, payment_total: e.target.value }))}
                  placeholder={defaultFeeStr || 'e.g. 534.00'}
                  className="bg-white"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {feeBreakdownLabel(data?.course?.sections, settings) || 'Adjust for special deals.'}
                </p>
              </div>
              <div>
                <Label htmlFor="t-received">
                  Deposit paid (£) <span className="text-rose-600" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="t-received"
                  data-testid="trainee-payment-received"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.payment_received}
                  onChange={(e) => setForm(f => ({ ...f, payment_received: e.target.value }))}
                  placeholder="e.g. 120.00"
                  required
                  className="bg-white"
                />
                {Number(form.payment_received) > Number(form.payment_total || 0) && Number(form.payment_total || 0) > 0 ? (
                  <p className="text-[11px] font-semibold text-rose-600 mt-1" data-testid="trainee-deposit-warning">
                    Deposit (£{Number(form.payment_received).toFixed(2)}) is more than the course fee (£{Number(form.payment_total).toFixed(2)}) — double-check before saving.
                  </p>
                ) : (
                  <p className="text-[10px] text-gray-500 mt-1">Required. Use 0 if it&apos;s a free / complimentary place.</p>
                )}
              </div>
            </div>
            {/* Live-computed balance chip — updates as staff types either
                field so the door-taker sees the outstanding amount in real
                time and can double-check it matches the customer's card.
                When extra courses are ticked we compute the total across
                all courses and apply the discount % if selected. */}
            {(() => {
              const extras = form.extra_course_ids
                .map((id) => otherCourses.find((c) => c.id === id))
                .filter(Boolean);
              const hasExtras = extras.length > 0;
              const courseCount = 1 + extras.length;
              const currentFee = Number(form.payment_total || 0);
              const extraFeesGross = extras.reduce((sum, c) => {
                const f = defaultCourseFeeForSections(c.sections, settings);
                return sum + (f || 0);
              }, 0);
              const gross = currentFee + extraFeesGross;
              const discPct = hasExtras && form.apply_multi_discount
                ? multiCourseDiscountPercent(courseCount, settings) : 0;
              const discAmt = Math.round(gross * discPct * 100) / 100;
              const net = Math.max(0, gross - discAmt);
              const dep = form.payment_received === '' ? null : Number(form.payment_received);
              const owed = dep === null || !Number.isFinite(dep) ? null : Math.max(0, net - dep);
              if (!Number.isFinite(gross) || gross <= 0) {
                return (
                  <p className="text-[11px] text-gray-500 -mt-1" data-testid="trainee-balance-hint">
                    Enter the course fee to see totals.
                  </p>
                );
              }
              return (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1 text-sm" data-testid="trainee-balance-chip">
                  <div className="flex justify-between text-gray-700">
                    <span>{hasExtras ? `${courseCount} courses` : 'Course fee'} (gross)</span>
                    <span className="tabular-nums">£{gross.toFixed(2)}</span>
                  </div>
                  {discAmt > 0 && (
                    <div className="flex justify-between text-emerald-700 font-medium" data-testid="trainee-discount-line">
                      <span>Multi-course discount ({Math.round(discPct * 1000) / 10}%)</span>
                      <span className="tabular-nums">− £{discAmt.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1 text-gray-900 font-semibold">
                    <span>Total to pay</span>
                    <span className="tabular-nums">£{net.toFixed(2)}</span>
                  </div>
                  {owed !== null && (
                    <div className={`flex justify-between font-bold ${owed === 0 ? 'text-emerald-800' : 'text-amber-800'}`}>
                      <span>Balance owed on the day</span>
                      <span className="tabular-nums">£{owed.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div>
              <Label htmlFor="t-notes">Notes (optional)</Label>
              <textarea
                id="t-notes"
                data-testid="trainee-notes"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="e.g. cash on the day · special deal agreed with AL · dietary needs"
                className="mt-1 w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400/40 bg-white"
                maxLength={2000}
              />
            </div>

            {/* Extra courses — book onto multiple dates in one go. Ticking
                more than one activates the optional multi-course discount
                checkbox below. */}
            {otherCourses.length > 0 && (
              <div className="border-t pt-3">
                <Label className="text-sm font-semibold">Also book onto…</Label>
                <p className="text-[11px] text-gray-500 mb-2">Tick every course this trainee is booking today. Fee is calculated across all of them.</p>
                <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                  {otherCourses.map((c) => {
                    const feeG = defaultCourseFeeForSections(c.sections, settings);
                    const checked = form.extra_course_ids.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${checked ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`extra-course-row-${c.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setForm(f => ({
                            ...f,
                            extra_course_ids: checked
                              ? f.extra_course_ids.filter(x => x !== c.id)
                              : [...f.extra_course_ids, c.id],
                          }))}
                          data-testid={`extra-course-checkbox-${c.id}`}
                        />
                        <span className="flex-1 truncate">
                          <strong className="text-gray-900">{c.name}</strong>
                          <span className="text-gray-500"> · {c.date}</span>
                        </span>
                        {feeG != null && <span className="tabular-nums text-gray-700">£{feeG.toFixed(2)}</span>}
                      </label>
                    );
                  })}
                </div>
                {form.extra_course_ids.length >= 1 && (
                  <label className="mt-3 flex items-start gap-2 text-xs text-gray-700 cursor-pointer" data-testid="multi-discount-toggle">
                    <input
                      type="checkbox"
                      checked={form.apply_multi_discount}
                      onChange={(e) => setForm(f => ({ ...f, apply_multi_discount: e.target.checked }))}
                      className="mt-0.5"
                    />
                    <span>
                      <strong>Apply multi-course discount</strong> ({(1 + form.extra_course_ids.length) === 2 ? '5%' : '10%'})
                      <span className="block text-gray-500">Off by default — we prefer to sell at full price. Only apply if the trainee negotiated it.</span>
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* Free City & Guilds certificate — recorded for the revenue
                report so we can see how many were given away in a period. */}
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer border-t pt-3" data-testid="free-cg-toggle">
              <input
                type="checkbox"
                checked={form.free_city_guilds}
                onChange={(e) => setForm(f => ({ ...f, free_city_guilds: e.target.checked }))}
                className="mt-0.5"
              />
              <span>
                <strong>Free City &amp; Guilds certificate</strong>
                <span className="block text-gray-500">Tick if you're giving one away as an incentive. Tracked in the revenue report.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingOne(false)} disabled={busy}>Cancel</Button>
            <Button onClick={() => addOne()} disabled={busy} data-testid="trainee-add-submit" className="text-white font-bold" style={{ background: palette.primary }}>
              {busy ? 'Adding…' : 'Add & send welcome'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate-email confirmation — shown when the typed email is already
          registered as an Ox Resin user. Staff can proceed (adds as a repeat
          booking; the existing account is reused and course-brand access is
          unioned onto it) or cancel and fix the email. */}
      <Dialog open={!!duplicateInfo} onOpenChange={(o) => !o && setDuplicateInfo(null)}>
        <DialogContent
          data-testid="duplicate-email-dialog"
          className="!bg-white max-w-md"
          style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: palette.dark }}>
              This email is already on Ox Resin
            </DialogTitle>
            <DialogDescription>
              An account exists for <strong>{duplicateInfo?.user?.email}</strong> — please double-check before you add this trainee as a repeat booking.
            </DialogDescription>
          </DialogHeader>
          {duplicateInfo?.user && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm space-y-1">
                <div><span className="text-gray-500">Name:</span> <span className="font-semibold">{duplicateInfo.user.name || '—'}</span></div>
                {duplicateInfo.user.company_name && <div><span className="text-gray-500">Company:</span> {duplicateInfo.user.company_name}</div>}
                {duplicateInfo.user.phone && <div><span className="text-gray-500">Phone:</span> {duplicateInfo.user.phone}</div>}
                <div><span className="text-gray-500">Role:</span> {duplicateInfo.user.role || 'installer'}</div>
                <div>
                  <span className="text-gray-500">Past courses:</span>{' '}
                  <span className="font-semibold">{duplicateInfo.bookings_count || 0}</span>
                </div>
              </div>
              {(duplicateInfo.bookings || []).slice(0, 3).map((b) => (
                <div key={b.id} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200">
                  <span className="font-mono">{b.course?.reference || b.course?.id}</span>{' '}
                  {b.course?.name || 'course'} · {b.course?.date}
                  {b.balance_paid_at ? ' · paid' : b.balance_owed ? ` · £${Number(b.balance_owed).toFixed(2)} owed` : ''}
                </div>
              ))}
              <div className="text-[11px] text-gray-500 pt-1 border-t">
                Proceeding will use the <strong>existing account</strong> — no duplicate is created. Any brand access
                the course grants will be added to their current account.
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Link
              to={`/staff/trainees/${encodeURIComponent(duplicateInfo?.user?.email || '')}`}
              className="text-sm text-gray-600 hover:text-gray-900 underline mr-auto"
              data-testid="duplicate-view-profile"
            >
              View full profile →
            </Link>
            <Button variant="outline" onClick={() => setDuplicateInfo(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => addOne({ skipDuplicateCheck: true })}
              disabled={busy}
              data-testid="duplicate-proceed"
              className="text-white font-bold"
              style={{ background: palette.primary }}
            >
              {busy ? 'Adding…' : 'Add as repeat booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
